/**
 * Natural sort utility functions
 */

/**
 * Natural sort comparison function that handles numbers within strings properly
 * Example: "item1", "item2", "item10" instead of "item1", "item10", "item2"
 * @param a First string to compare
 * @param b Second string to compare
 * @returns -1 if a < b, 1 if a > b, 0 if equal
 */
export const naturalSort = (a: string, b: string): number => {
  // Convert to lowercase for case-insensitive comparison
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  
  // Split into chunks of letters and numbers
  const aChunks = aLower.match(/\d+|\D+/g) || [];
  const bChunks = bLower.match(/\d+|\D+/g) || [];
  
  // Compare chunk by chunk
  const maxLength = Math.max(aChunks.length, bChunks.length);
  
  for (let i = 0; i < maxLength; i++) {
    const aChunk = aChunks[i] || '';
    const bChunk = bChunks[i] || '';
    
    // If one chunk is missing, the shorter string comes first
    if (!aChunk && bChunk) return -1;
    if (aChunk && !bChunk) return 1;
    
    // Check if both chunks are numbers
    const aIsNumber = /^\d+$/.test(aChunk);
    const bIsNumber = /^\d+$/.test(bChunk);
    
    if (aIsNumber && bIsNumber) {
      // Compare as numbers
      const aNum = parseInt(aChunk, 10);
      const bNum = parseInt(bChunk, 10);
      if (aNum !== bNum) {
        return aNum - bNum;
      }
    } else {
      // Compare as strings
      if (aChunk !== bChunk) {
        return aChunk.localeCompare(bChunk);
      }
    }
  }
  
  return 0;
};

/**
 * Sort building attributes by building, then template, then name (using natural sort)
 */
export const sortBuildingAttributes = <T extends {
  name: string;
  template: { category: string };
  buildings: Array<{ id: string }>;
}>(attributes: T[]): T[] => {
  return [...attributes].sort((a, b) => {
    // First sort by building ID (take the first building if multiple)
    const aBuildingId = a.buildings[0]?.id || '';
    const bBuildingId = b.buildings[0]?.id || '';
    
    if (aBuildingId !== bBuildingId) {
      return naturalSort(aBuildingId, bBuildingId);
    }
    
    // Then sort by template category
    const aTemplate = a.template.category;
    const bTemplate = b.template.category;
    
    if (aTemplate !== bTemplate) {
      return naturalSort(aTemplate, bTemplate);
    }
    
    // Finally sort by name using natural sort
    return naturalSort(a.name, b.name);
  });
};
